import React, { useState, useEffect } from 'react';
import Dialog from '../../dialog';
import StatusIndicator from '../../status/status-indicator';
import { RoomWrapper } from '../../room';
import Icon from '../../../atoms/icon';
import {
  faBell,
  faCalendarPlus,
  faCheck,
  faMoon,
  faTimesCircle,
  faUser
} from '@fortawesome/pro-solid-svg-icons';
import { FormatPhone } from '../../../../libs/format';
import { Container, Row, Col } from '../../../../grid';
import SnoozeForm from './snooze-form';
import { useRequestOnEvent } from '../../../../libs/hooks/useRequestJSON';
import ResolveForm from './resolve-form';
import EventSummary from './event-summary';
import Button from '../../../molecules/button';
import ButtonGroup from '../../button-group';
import { useTerms } from '../../../../contexts/terms';
import { useMetaData } from '../../../../contexts/metadata';
import { useTriagePatient } from '../../../../contexts/triage/patient';
import {
  AddressEventsProps,
  AddressScopeType,
  AddressTypeString,
  Response
} from './types';

const AddressEvents: React.FC<AddressEventsProps> = ({
  isOpen,
  close,
  payload
}) => {
  // Form request to post, run is passed down to form children
  // resetRequest() is called each time the addressType changes or Dialog closes
  const { data, isLoading, error, run, resetRequest } = useRequestOnEvent<
    Response,
    Response
  >();

  // Flag to prevent user action from accidentally closing Dialog as it transitions into view
  const [isInTransition, setIsInTransition] = useState(false);

  // Pull custom account snooze and resolve options
  const { terms } = useTerms();
  const { settings: accountSettings } = useMetaData();
  const snoozeOptions = accountSettings?.snoozeOptions;
  const resolveOptions = accountSettings?.resolveOptions;

  const [addressType, setAddressType] = useState(payload.addressType);
  const [addressScope, setAddressScope] = useState<AddressScopeType>(
    payload.alert ? 'alert' : 'patient'
  );

  // Get patient data for status display
  const { patientData, updatePatientId } = useTriagePatient();
  const patient = patientData ? patientData.patient : null;
  const triageCategoryId = patientData ? patientData.category : null;
  const triageAlertIds = patientData
    ? patientData.alerts.map((alert) => alert.id)
    : null;

  // Ensure patient data is updated in this Dialog, as the Dialog itself persists
  useEffect(() => {
    if (updatePatientId) {
      updatePatientId(payload.patientId > 0 ? payload.patientId : null);
    }
  }, [payload.patientId, updatePatientId]);

  // Data to help pre-select alerts
  const alert = payload.alert || null;
  const initialSelectedAlerts = alert ? [alert.id] : null;

  // Allow alerts to be selected over time
  const [selectedAlertIds, setSelectedAlertIds] = useState<number[]>(
    initialSelectedAlerts
  );
  const [note, setNote] = useState<string>('');

  const handleAddressTypeChange = (value: AddressTypeString) => {
    setAddressType(value as AddressTypeString);
    resetRequest();
  };
  const handleAddressScopeChange = (value: AddressScopeType) => {
    setAddressScope(value as AddressScopeType);
    resetRequest();
  };
  const handleClose = () => {
    resetRequest();
    close();
  };
  const handleNoteChanged = (newNote: string) => {
    if (newNote !== note) {
      setNote(newNote);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      maxWidth="1500px"
      onClose={handleClose}
      onOpening={() => setIsInTransition(true)}
      onOpened={() => setIsInTransition(false)}
      canOutsideClickClose={!isInTransition}
      canEscapeKeyClose={!isInTransition}
      className="pb-0"
      forceScrollY
    >
      <RoomWrapper
        patientId={patient ? patient.id : null}
        borderWidth="4"
        className="rounded"
        includeMembers
        joinRoom
      >
        <div className="py-5 px-6 font-bold relative">
          <Icon icon={faCalendarPlus} className="mr-4" />
          Address Events
          <Button
            style={{
              top: '1px'
            }}
            className="absolute right-0"
            icon={<Icon icon={faTimesCircle} />}
            variant="minimal"
            onClick={handleClose}
          />
        </div>
        <div className="py-3 px-4 border-t-solid border-t-1 border-light-grey">
          <h2 className="mt-0 mb-2 font-serif inline-block">
            {/* Stick with primary triage category for Address Events modal */}
            <StatusIndicator
              category={triageCategoryId}
              isPatient
              title={false}
            />
            {patient
              ? `${patient.firstName}${
                  patient.middleInitial ? ` ${patient.middleInitial}.` : ''
                } ${patient.lastName}`
              : null}
          </h2>
          {patient ? (
            <span className="ml-2">{FormatPhone(patient.phone)}</span>
          ) : null}
        </div>
        <div className="border-t-solid border-t-1 border-light-grey">
          <Container fluid>
            <Row>
              <Col md={10} lg={11}>
                <Row className="border-b-solid border-b-1 border-light-grey pb-6">
                  <Col md={12}>
                    <label
                      className="font-bold inline-block mt-8 mb-2"
                      htmlFor="addressType"
                      id="addressTypeLabel"
                    >
                      Choose Mode
                    </label>
                    <ButtonGroup
                      aria-labelledby="addressTypeLabel"
                      id="addressType"
                      fill
                    >
                      <Button
                        active={addressType === 'snooze'}
                        onClick={handleAddressTypeChange.bind(this, 'snooze')}
                      >
                        <Icon className="mr-3" icon={faMoon} />
                        Snooze
                      </Button>
                      <Button
                        active={addressType === 'resolve'}
                        onClick={handleAddressTypeChange.bind(this, 'resolve')}
                      >
                        <Icon className="mr-3" icon={faCheck} />
                        Resolve
                      </Button>
                    </ButtonGroup>
                  </Col>
                  <Col md={12}>
                    <label
                      className="font-bold inline-block mt-8 mb-2"
                      htmlFor="addressScope"
                      id="addressScopeLabel"
                    >
                      Choose Scope
                    </label>
                    <ButtonGroup
                      aria-labelledby="addressScopeLabel"
                      id="addressScope"
                      fill
                    >
                      <Button
                        active={addressScope === 'alert'}
                        onClick={handleAddressScopeChange.bind(this, 'alert')}
                      >
                        <Icon className="mr-3" icon={faBell} />
                        Alert
                      </Button>
                      <Button
                        active={addressScope === 'patient'}
                        onClick={handleAddressScopeChange.bind(this, 'patient')}
                      >
                        <Icon className="mr-3" icon={faUser} />
                        {terms.patient.capitalized}
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
                <div className="mt-8 ml-4">
                  {addressType === 'snooze' ? (
                    <SnoozeForm
                      addressScope={addressScope}
                      patient={patient}
                      alert={alert}
                      selectedAlertIds={selectedAlertIds}
                      note={note}
                      onNoteChanged={handleNoteChanged}
                      options={snoozeOptions}
                      data={data}
                      initialFormData={payload ? payload.formData : null}
                      error={error}
                      isLoading={isLoading}
                      run={run}
                      onSnoozed={(until: string) => {
                        if (payload?.onSuccess) {
                          payload.onSuccess(until);
                        }
                        handleClose();
                      }}
                    />
                  ) : (
                    <ResolveForm
                      addressScope={addressScope}
                      patient={patient}
                      alert={alert}
                      selectedAlertIds={selectedAlertIds}
                      note={note}
                      onNoteChanged={handleNoteChanged}
                      options={resolveOptions}
                      data={data}
                      error={error}
                      isLoading={isLoading}
                      run={run}
                      onResolved={() => {
                        if (payload?.onSuccess) {
                          payload.onSuccess();
                        }
                        handleClose();
                      }}
                    />
                  )}
                </div>
              </Col>
              <Col md={14} lg={13} className="bg-light-grey">
                {patient && (
                  <EventSummary
                    addressType={addressType}
                    isAddressingPatient={addressScope === 'patient'}
                    patient={patient}
                    alertIds={triageAlertIds}
                    selectedAlertIds={selectedAlertIds}
                    setSelectedAlertIds={setSelectedAlertIds}
                  />
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </RoomWrapper>
    </Dialog>
  );
};

export default AddressEvents;
